import { useState, useEffect } from "react";
import "@/App.css";
import useAnalytics from "./lib/analytics";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CompanyProvider } from "./context/CompanyContext";
import { LanguageProvider } from "./i18n";
import { Toaster } from "sonner";
import useIdleReload from "./lib/useIdleReload";

import Home from "./pages/HomeNew";
import PricingPage from "./pages/PricingPage";
import Contacto from "./pages/Contacto";
import Solucao from "./pages/marketing/Solucao";
import Oferta from "./pages/marketing/Oferta";
import LimpezaIncluida from "./pages/marketing/LimpezaIncluida";
import Historia from "./pages/marketing/Historia";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Gallery from "./pages/Gallery";
import Acesso from "./pages/Acesso";
import Testimonials from "./pages/Testimonials";
import Nps from "./pages/Nps";
import Termos from "./pages/legal/Termos";
import Privacidade from "./pages/legal/Privacidade";
import CookiesPolicy from "./pages/CookiesPolicy";
import PaymentsPolicy from "./pages/PaymentsPolicy";
import ContractSignPage from "./pages/contracts/ContractSignPage";
import ContractSignedSuccess from "./pages/contracts/ContractSignedSuccess";
import { ForgotPassword, ResetPassword } from "./pages/PasswordRecovery";
import SocialProof from "./components/SocialProof";
import CookieBanner from "./components/CookieBanner";
import ClientApp from "./pages/client/ClientApp";
import AdminApp from "./pages/admin/AdminApp";
import EmployeeApp from "./pages/employee/EmployeeApp";
import SplashIntro from "./components/SplashIntro";
import ErrorBoundary from "./components/ErrorBoundary";
import ForceChangePassword from "./pages/ForceChangePassword";

function RoleRoute({ roles, children }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <div className="min-h-screen bg-[#041f1b] grid place-items-center text-[#d4af37]" data-testid="loading">A carregar…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  // Force first-login password change. Until cleared, the user can only
  // access /alterar-password. This protects admin-created accounts whose
  // initial password was sent by email — they must rotate it before doing
  // anything else.
  if (user.must_change_password && pathname !== "/alterar-password") {
    return <Navigate to="/alterar-password" replace />;
  }
  return children;
}

// Splash shown once per session; it now dismisses in place and preserves the
// current route (deep links like /admin or /cliente/alojamentos work correctly).
function AppShell() {
  // Refresh the page after 5 minutes of true inactivity (no clicks, keys,
  // scrolls, touches). Keeps long-open tabs in sync with new deploys and
  // fresh data without the user having to pull-to-refresh.
  useIdleReload(5);

  const [showSplash, setShowSplash] = useState(() => {
    try {
      // Public, deep-linked routes (email magic links, contract signing, etc.)
      // MUST go straight to content. The 6-second brand splash would otherwise
      // hide the contract from the client until it auto-dismisses, which feels
      // broken — the email said "click to sign" and the user expects the
      // contract immediately.
      const p = (window.location.pathname || "").toLowerCase();
      const isDeepPublicLink =
        p.startsWith("/contratos/assinar") ||
        p.startsWith("/acesso") ||
        p.startsWith("/reset-password") ||
        p.startsWith("/recuperar-password");
      if (isDeepPublicLink) return false;
      return !sessionStorage.getItem("luxstay-splash-v2");
    } catch { return true; }
  });

  function finishSplash() {
    try { sessionStorage.setItem("luxstay-splash-v2", "1"); } catch { /* ignore */ }
    setShowSplash(false);
    // Previously: if (location.pathname !== "/") navigate("/", { replace: true });
    // That destroyed deep links (e.g. opening /admin or /cliente/alojamentos would
    // redirect back to / as soon as the splash auto-dismissed). Splash now simply
    // closes in place, preserving the user's intended route.
  }

  return (
    <>
      <ScrollToTop />
      <AnalyticsTracker />
      {showSplash && <SplashIntro onDone={finishSplash} />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/precos" element={<PricingPage />} />
        <Route path="/contacto" element={<Contacto />} />
        <Route path="/login" element={<Login />} />
        <Route path="/registo" element={<Register />} />
        <Route path="/recuperar-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/galeria" element={<Gallery />} />
        <Route path="/acesso" element={<Acesso />} />
        <Route path="/testemunhos" element={<Testimonials />} />
        <Route path="/nps" element={<Nps />} />
        <Route path="/termos" element={<Termos />} />
        <Route path="/privacidade" element={<Privacidade />} />
        <Route path="/cookies" element={<CookiesPolicy />} />
        <Route path="/pagamentos" element={<PaymentsPolicy />} />
        <Route path="/contratos/assinar/:token/concluido" element={<ContractSignedSuccess />} />
        <Route path="/contratos/assinar/:token" element={<ContractSignPage />} />
        <Route path="/solucao" element={<Solucao />} />
        <Route path="/oferta" element={<Oferta />} />
        <Route path="/limpeza-incluida" element={<LimpezaIncluida />} />
        <Route path="/historia" element={<Historia />} />
        <Route path="/alterar-password" element={<RoleRoute><ForceChangePassword /></RoleRoute>} />
        <Route path="/cliente/*" element={<RoleRoute roles={["client"]}><ClientApp /></RoleRoute>} />
        <Route path="/admin/*" element={<RoleRoute roles={["admin"]}><AdminApp /></RoleRoute>} />
        <Route path="/funcionario/*" element={<RoleRoute roles={["employee"]}><EmployeeApp /></RoleRoute>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <PublicChatMount />
    </>
  );
}

function PublicChatMount() {
  // Floating AI assistant + (home only, non-authenticated) silent social
  // proof banner. The marketing radio was removed; social proof is the
  // quiet replacement that nudges visitors towards conversion.
  const { pathname } = useLocation();
  const { user } = useAuth();
  const adminOrEmployee = pathname.startsWith("/admin") || pathname.startsWith("/funcionario");
  if (adminOrEmployee) return null;
  const showSocialProof = pathname === "/" && !user;
  return (
    <>
      {showSocialProof && <SocialProof />}
      <CookieBanner />
    </>
  );
}

/**
 * Resets the scroll position to the top of the page on every route
 * change. Without this, React Router preserves the previous scroll
 * (which is fine for a same-page anchor navigation, but jarring when
 * you click a sidebar item and arrive halfway down the new page).
 *
 * Skips routes where the user is mid-flow scrolling (e.g. while reading
 * the contract on /contratos/assinar) — a hash navigation on those
 * pages would otherwise jump unexpectedly.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      window.scrollTo(0, 0);
    }
    document.querySelectorAll("[data-scroll-root]").forEach(el => {
      el.scrollTop = 0;
    });
  }, [pathname]);
  return null;
}

function AnalyticsTracker() {
  useAnalytics();
  return null;
}

function Router() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <AuthProvider>
          <CompanyProvider>
            <Router />
            <Toaster theme="dark" position="top-right" richColors />
          </CompanyProvider>
        </AuthProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}
